import {
    ITEM_LOCATION_TYPES,
    ROADMAP_REQUEST_FIELDS,
    ROADMAP_TYPES,
    SPECIAL_ACTION_IDS,
} from '@/roadmaps/constants/roadmaps.constants';
import { useDraggable } from '@/roadmaps/use/use-draggable';
import { setPendingActionNone } from '@/roadmaps/use/use-pending-actions';
import * as api from '@/vuex/modules/roadmaps/api/roadmaps.api';
import { asyncQueue } from '@/vuex/modules/roadmaps/async-queue/async-queue';
import each from 'lodash/each';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import reject from 'lodash/reject';
import intersectionWith from 'lodash/intersectionWith';
// eslint-disable-next-line internal-rules/composable-consistent-naming
import { unref } from 'vue';
import Vue from 'vue';

const state = {
    items: {},
    itemPromises: {},
};

const getters = {
    items: (state) => {
        const { useVocItemsStore } = require('@/stores/voc-items.store'); // TODO: remove require when module is converted to Pinia
        const itemsWithQueuedUpdates = {
            ...state.items,
            ...asyncQueue.queuedUpdates.items,
        };

        return mapValues(itemsWithQueuedUpdates, (item) => {
            if (item.type !== ITEM_LOCATION_TYPES.IDEA.key) return item;

            const vocItem = useVocItemsStore().vocItemById(item.importedItemId);
            if (!vocItem) {
                return item;
            }

            const fieldsToSync = {
                title: vocItem.formData[0].content,
            };

            return {
                ...item,
                ...fieldsToSync,
            };
        });
    },
    itemById: (_state, getters) => (itemId) => getters.items[itemId],
    itemsByParentId: (_state, getters) => (parentId) => filter(getters.items, (item) => item.parentId === parentId),
    swimlaneItemsByRow: (_state, getters) => (swimlaneId, row) =>
        filter(getters.items, (item) => item.row === row && item.parentId === swimlaneId),
    swimlaneItemsBelowRow: (_state, getters) => (swimlaneId, row) =>
        filter(getters.items, (item) => item.row > row && item.parentId === swimlaneId),
    childItemsWithoutDraggedItem: (_state, getters) => (parentId) => {
        const childrenByParent = getters.itemsByParentId(parentId);
        const { draggedItem } = useDraggable();
        if (!unref(draggedItem)) return childrenByParent;

        const childrenNotBeingDragged = reject(childrenByParent, (child) => {
            return unref(draggedItem).id === child.id;
        });

        return childrenNotBeingDragged;
    },
    backlogItems: (_state, getters) => filter(getters.items, (item) => item.onBacklog),
    itemsByColumnId: (_state, getters) => (columnId) => filter(getters.items, (item) => item.columnId === columnId),
    itemsByParentIdAndColumnId: (_state, getters) => (parentId, columnId) =>
        filter(getters.items, (item) => item.columnId === columnId && item.parentId === parentId),
    itemsByParentIdAndColumnIdWithoutDraggedItem: (_state, getters) => (parentId, columnId) => {
        const itemsByParentAndColumnId = getters.itemsByParentIdAndColumnId(parentId, columnId);
        const { draggedItem } = useDraggable();
        if (!unref(draggedItem)) return itemsByParentAndColumnId;

        const itemsNotBeingDragged = reject(itemsByParentAndColumnId, (item) => {
            return unref(draggedItem).id === item.id;
        });

        return itemsNotBeingDragged;
    },
    itemsByType: (_state, getters) => (type) => filter(getters.items, (item) => item.type === type),
};

const mutations = {
    setStatus (state, status) {
        state.status = status;
    },
    deleteByKey (state, itemId) {
        Vue.delete(state.items, itemId);
    },
    setMap (state, { items }) {
        state.items = mapValues(items, (item) => Object.freeze(item));
    },
    updateMap (state, { items }) {
        const itemsToUpdate = mapValues(items, (item) => {
            return Object.assign({}, state.items[item.id], item);
        });

        const itemsUpdated = Object.assign({}, state.items, itemsToUpdate);

        state.items = mapValues(itemsUpdated, (item) => Object.freeze(item));
    },
    updateMapAtId (state, { id, item }) {
        const frozenItem = Object.freeze(Object.assign({}, state.items[id], item));
        Vue.set(state.items, id, frozenItem);
    },
    updateMapAtKey (state, { id, key, value }) {
        const frozenItem = Object.freeze(Object.assign({}, state.items[id], { [key]: value }));
        Vue.set(state.items, id, frozenItem);
    },
    updateItemPromises (state, { itemId, promise }) {
        if (promise) {
            Vue.set(state.itemPromises, itemId, promise);
        } else if (state.itemPromises[itemId]) {
            Vue.delete(state.itemPromises, itemId);
        }
    },
};

const actions = {
    async loadOne ({ state, commit }, { roadmapId, itemId }) {
        if (state.itemPromises[itemId]) {
            return state.itemPromises[itemId];
        }

        try {
            const promise = api.fetchRoadmapItem(roadmapId, itemId);
            commit('updateItemPromises', { itemId, promise });
            const item = await promise;
            commit('updateMapAtId', { id: itemId, item: { ...item, roadmapId } });
        } catch (error) {
            commit('updateMapAtId', { id: itemId, item: { error } });
            throw error;
        } finally {
            commit('updateItemPromises', { itemId, promise: null });
        }
    },
    loadMany ({ dispatch }, { items }) {
        const promises = map(items, (item) => {
            return dispatch('loadOne', { roadmapId: item.roadmapId, itemId: item.id });
        });

        return Promise.allSettled(promises);
    },
    async fetchRoadmapBacklog ({ commit, rootGetters }) {
        const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];
        const backlogItems = await api.fetchRoadmapBacklog(currentRoadmap.id);

        commit('updateMap', { items: backlogItems });

        return backlogItems;
    },
    async createItem ({ commit, rootGetters }, { item, resolvingItems = [] }) {
        const task = async () => {
            const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];
            const payload = {
                item: pick(
                    item,
                    currentRoadmap.type === ROADMAP_TYPES.TIMELINE
                        ? ROADMAP_REQUEST_FIELDS.timelineItemCreate
                        : ROADMAP_REQUEST_FIELDS.columnItemCreate,
                ),
                resolveItemLocations: map(resolvingItems, (item) => {
                    return pick(
                        item,
                        currentRoadmap.type === ROADMAP_TYPES.TIMELINE
                            ? ROADMAP_REQUEST_FIELDS.timelineItemLocationsUpdate
                            : ROADMAP_REQUEST_FIELDS.columnItemLocationsUpdate,
                    );
                }),
                roadmapVersionTimestamp: currentRoadmap.lastUpdatedAt,
            };

            const responseItems = await api.createRoadmapItem(currentRoadmap.id, payload);
            commit('updateMap', { items: responseItems });
            each(responseItems, (item) => {
                setPendingActionNone(item.id);
            });

            return responseItems;
        };

        return asyncQueue.enqueue({
            task,
            updates: {
                [SPECIAL_ACTION_IDS.PLACEHOLDER_ITEM]: { ...item, id: SPECIAL_ACTION_IDS.PLACEHOLDER_ITEM },
                ...keyBy(resolvingItems, 'id'),
            },
            updateType: 'items',
        });
    },
    async updateItem ({ commit, rootGetters }, itemToUpdate) {
        const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];
        const payload = {
            item: pick(itemToUpdate, ROADMAP_REQUEST_FIELDS.item),
        };
        const item = await api.updateRoadmapItem(currentRoadmap.id, payload);
        commit('updateMapAtId', { id: itemToUpdate.id, item });
    },
    async updateLocations ({ dispatch, rootGetters }, itemsToUpdate) {
        const task = async () => {
            const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];
            const payload = {
                itemLocations: map(itemsToUpdate, (item) => {
                    return pick(
                        item,
                        currentRoadmap.type === ROADMAP_TYPES.TIMELINE
                            ? ROADMAP_REQUEST_FIELDS.timelineItemLocationsUpdate
                            : ROADMAP_REQUEST_FIELDS.columnItemLocationsUpdate,
                    );
                }),
                roadmapVersionTimestamp: currentRoadmap.lastUpdatedAt,
            };
            const updatedRoadmap = await api.updateItemLocations(currentRoadmap.id, payload);

            dispatch('roadmaps/roadmaps/setOne', updatedRoadmap, { root: true });

            return updatedRoadmap;
        };

        return asyncQueue.enqueue({
            task,
            updates: keyBy(itemsToUpdate, 'id'),
            updateType: 'items',
        });
    },
    async promoteItems ({ commit, rootGetters }, { parent, children, importedItem, resolvingItems }) {
        const task = async () => {
            const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];

            const payload = {
                parentItem: pick(parent, ROADMAP_REQUEST_FIELDS.item),
                childItemLocations: children.map((child) => pick(child, ROADMAP_REQUEST_FIELDS.item)),
                resolveItemLocations: resolvingItems.map((item) => pick(item, ROADMAP_REQUEST_FIELDS.item)),
                roadmapVersionTimestamp: currentRoadmap.lastUpdatedAt,
            };

            if (importedItem) payload.importedItem = pick(importedItem, ROADMAP_REQUEST_FIELDS.item);

            const resItems = await api.promoteItems(currentRoadmap.id, payload);

            commit('updateMap', { items: resItems });

            return resItems;
        };

        const childrenWithPromoteParentId = map(children, (child) => ({
            ...child,
            parentId: SPECIAL_ACTION_IDS.PROMOTE_PARENT,
        }));

        const asyncQueueUpdates = {
            ...keyBy(childrenWithPromoteParentId, 'id'),
            ...keyBy(resolvingItems, 'id'),
            [SPECIAL_ACTION_IDS.PROMOTE_PARENT]: { ...parent, id: SPECIAL_ACTION_IDS.PROMOTE_PARENT },
        };
        if (importedItem) asyncQueueUpdates[importedItem.id] = importedItem;

        return asyncQueue.enqueue({
            task,
            updates: asyncQueueUpdates,
            updateType: 'items',
        });
    },
    async deleteItem ({ dispatch, rootGetters }, itemId) {
        const task = async () => {
            const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];
            const payload = {
                roadmapVersionTimestamp: currentRoadmap.lastUpdatedAt,
            };
            const updatedRoadmap = await api.deleteRoadmapItem(currentRoadmap.id, itemId, payload);

            dispatch('roadmaps/roadmaps/setOne', updatedRoadmap, { root: true });

            return updatedRoadmap;
        };

        return asyncQueue.enqueue({
            task,
            updates: {},
            updateType: 'items',
        });
    },
    async createIdeaOnRoadmap ({ commit, rootGetters }, { ideaToCreateOnRoadmap, resolvingItems }) {
        const task = async () => {
            const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];
            const payload = {
                item: pick(
                    ideaToCreateOnRoadmap,
                    currentRoadmap.type === ROADMAP_TYPES.TIMELINE
                        ? ROADMAP_REQUEST_FIELDS.timelineItemCreate
                        : ROADMAP_REQUEST_FIELDS.columnItemCreate,
                ),
                resolveItemLocations: resolvingItems.map((item) => pick(item, ROADMAP_REQUEST_FIELDS.item)),
                roadmapVersionTimestamp: currentRoadmap.lastUpdatedAt,
            };

            const responseItems = await api.createIdeaItemOnRoadmap(
                currentRoadmap.id,
                ideaToCreateOnRoadmap.importedItemId,
                payload,
            );
            commit('updateMap', { items: responseItems });
            each(responseItems, (item) => {
                setPendingActionNone(item.id);
            });

            return responseItems;
        };

        return asyncQueue.enqueue({
            task,
            updates: {
                [SPECIAL_ACTION_IDS.PLACEHOLDER_ITEM]: { ...ideaToCreateOnRoadmap, id: SPECIAL_ACTION_IDS.PLACEHOLDER_ITEM },
                ...keyBy(resolvingItems, 'id'),
            },
            updateType: 'items',
        });
    },
    async promoteIdeaToFeature ({ commit, getters, rootGetters }, { itemId }) {
        const roadmapItem = getters.itemById(itemId);

        const task = async () => {
            const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];

            const responseItem = await api.promoteIdeaToFeature(currentRoadmap.id, itemId);
            commit('updateMapAtId', { id: itemId, item: responseItem });

            return responseItem;
        };

        return asyncQueue.enqueue({
            task,
            updates: keyBy([roadmapItem], 'id'),
            updateType: 'items',
        });
    },
    async fetchRoadmapItemExternalIdeas ({ rootGetters }, { itemId }) {
        const { useVocItemsStore } = require('@/stores/voc-items.store'); // TODO: remove require when module is converted to Pinia
        const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];
        const vocItems = useVocItemsStore().list;

        const externalIdeas = await api.getRoadmapItemExternalIdeas(currentRoadmap.id, itemId);

        const matchedIdeas = intersectionWith(
            externalIdeas,
            vocItems,
            (externalIdea, vocItem) => externalIdea.externalId === vocItem.id,
        );

        return matchedIdeas;
    },
    setIsExpanded ({ commit }, { id, expanded }) {
        commit('updateMapAtKey', { id, key: 'isExpanded', value: expanded });
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
